import { useCampaignParents } from '../../../../useCampaignParents';
import { useAgencyCampaignContext } from '../../../../agencyCampaignContext';
import { AgencyCampaignSection } from '../../../../../agency/model';
import { createDemandClient } from '../../../../../agency/demandApi';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DashboardPageFormShell } from '@shigo-ui/dashboard-ui';
import { toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../../components/navigation/ReturnBackLink';
import { dashboardT } from '../../../../../../lib/messages';
import { returnFallback, useReturnNavigation } from '../../../../../../lib/returnNavigation';
import { readShigoAppConfig } from '../../../../../../lib/config';
import { useShigoSession } from '../../../../../../lib/useShigoSession';
import { createTargetingClient, type TargetingClient } from '../../../../../targeting/api';
import { createCampaignsClient } from '../../../api';
import {
  createMadBuyLineItemGeneralForm,
  serializeMadBuyLineItemGeneralForm,
  type MadBuyLineItemGeneralForm,
} from '../../../lineItemsModel';
import type { LineItemTargetingCsvKind } from '../../../lineItemTargetingCsvModel';
import {
  saveLineItemWithTargetingCsv,
  type LineItemTargetingCsvFailure,
} from '../../../saveLineItemWithTargetingCsv';
import { LineItemEditorForm } from '../LineItemEditorForm';
import { LineItemEditorErrorState, LineItemEditorSkeleton } from '../LineItemEditorStates';

type CampaignsClient = ReturnType<typeof createCampaignsClient>;
type SavedTargeting = Readonly<Record<string, unknown>> | null | undefined;

type LineItemEditorPageProps = {
  campaignId: string;
  client?: CampaignsClient | undefined;
  lineItemId?: string | undefined;
  targetingClient?: TargetingClient | undefined;
};

export function LineItemEditorPage({
  campaignId,
  client: providedClient,
  lineItemId,
  targetingClient: providedTargetingClient,
}: LineItemEditorPageProps) {
  const scope = useAgencyCampaignContext();
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const apiConfig = { apiBaseUrl: config.madBuyServiceUrl, networkId: config.networkId };
  const referenceClient = createCampaignsClient({
    config: apiConfig,
    accessToken: session?.accessToken ?? '',
  });
  const client =
    providedClient ??
    (scope
      ? {
          ...createDemandClient({
            config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
            accessToken: session?.accessToken ?? '',
            agencyId: scope.agency.id,
          }).campaigns,
          listConversionPixels: referenceClient.listConversionPixels,
        }
      : referenceClient);
  const targetingClient =
    providedTargetingClient ??
    createTargetingClient({
      config: apiConfig,
      agencyId: scope?.agency.id,
      rootApiConfig: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
      accessToken: session?.accessToken ?? '',
    });
  const queryClient = useQueryClient();
  const prefix = scope ? ['agency-demand', config.networkId, scope.agency.id] : [];
  const backFallback = scope
    ? returnFallback({
        to: '/a/$agencyId/campaigns/$section',
        params: { agencyId: scope.agency.id, section: AgencyCampaignSection.Campaigns },
        search: { campaign: campaignId },
      })
    : returnFallback({
        to: '/campaigns/campaigns/overview/$campaignId',
        params: { campaignId },
      });
  const { returnTo } = useReturnNavigation(backFallback);
  const lineItemQuery = useQuery({
    queryKey: [...prefix, 'campaigns', 'line-item', lineItemId],
    enabled: Boolean(session && lineItemId),
    queryFn: () => client.getLineItem(lineItemId ?? ''),
    refetchOnMount: 'always',
  });
  const campaignQuery = useQuery({
    queryKey: [...prefix, 'campaigns', 'campaign-list', 'detail', campaignId],
    enabled: Boolean(session),
    queryFn: () => client.getCampaign(campaignId),
  });
  useCampaignParents(campaignQuery.data?.brandId ?? campaignQuery.data?.brand?.id ?? '');
  // A create whose CSV import failed stays here; later saves update the created line item.
  const [createdLineItemId, setCreatedLineItemId] = useState<string>();
  const [savedTargetingAfterSave, setSavedTargetingAfterSave] = useState<SavedTargeting>();
  const [csvFailures, setCsvFailures] = useState<
    Partial<Record<LineItemTargetingCsvKind, LineItemTargetingCsvFailure>>
  >({});
  const savedLineItemId = lineItemId ?? createdLineItemId;
  const savedTargeting = savedTargetingAfterSave ?? lineItemQuery.data?.targeting;
  const save = useMutation({
    mutationFn: (form: MadBuyLineItemGeneralForm) => {
      if (lineItemId && lineItemQuery.data?.campaign?.id !== campaignId) {
        throw new Error('Line item does not belong to the route campaign');
      }
      const payload = serializeMadBuyLineItemGeneralForm(form, campaignId, { timezone: 'UTC' });
      return saveLineItemWithTargetingCsv({
        dependencies: {
          create: client.createLineItem,
          update: client.updateLineItem,
          get: client.getLineItem,
          importCsv: client.importLineItemTargetingCsv,
        },
        form,
        lineItemId: savedLineItemId,
        payload,
      });
    },
    onSuccess: async (result) => {
      setCsvFailures(result.failures);
      if (Object.keys(result.failures).length) {
        setCreatedLineItemId(lineItemId ? undefined : result.lineItemId);
        setSavedTargetingAfterSave(result.savedTargeting);
        const messages = Object.values(result.failures).flatMap((failure) => failure.messages);
        toast.error(dashboardT('targeting.lineItemCsv.partialError'), {
          description: messages.length ? messages.join('\n') : undefined,
        });
        void queryClient.invalidateQueries({ queryKey: [...prefix, 'campaigns', 'line-items'] });
        return;
      }
      toast.success(
        savedLineItemId
          ? dashboardT('madBuy.lineItems.updated')
          : dashboardT('madBuy.lineItems.created'),
      );
      try {
        await queryClient.invalidateQueries({ queryKey: [...prefix, 'campaigns', 'line-items'] });
        // The save settled the form, so the unsaved-changes guard must not ask again.
        await returnTo({ replace: true, ignoreBlocker: true });
      } catch {
        toast.error(dashboardT('madBuy.lineItems.redirectError'));
      }
    },
  });
  const waitingForFreshDetail =
    Boolean(lineItemId) && !lineItemQuery.isFetchedAfterMount && !lineItemQuery.isError;
  const loading = campaignQuery.isLoading || waitingForFreshDetail;
  const initialForm = resolveInitialForm(lineItemQuery.data);
  const campaignMismatch = Boolean(
    lineItemId && lineItemQuery.data && lineItemQuery.data.campaign?.id !== campaignId,
  );
  const failed =
    campaignQuery.isError ||
    (Boolean(lineItemId) && lineItemQuery.isError) ||
    campaignMismatch ||
    !initialForm;
  const brandId =
    lineItemQuery.data?.campaign?.brand?.id ??
    campaignQuery.data?.brandId ??
    campaignQuery.data?.brand?.id ??
    '';

  if (!session) return null;

  return (
    <DashboardPageFormShell
      title={
        lineItemId
          ? dashboardT('madBuy.lineItems.editTitle')
          : dashboardT('madBuy.lineItems.newTitle')
      }
      subtitle={
        lineItemQuery.data?.altId
          ? dashboardT('madBuy.lineItems.altId', { id: lineItemQuery.data.altId })
          : undefined
      }
      backAction={<ReturnBackLink fallback={backFallback} label={dashboardT('campaigns.back')} />}
    >
      {loading ? <LineItemEditorSkeleton /> : null}
      {failed ? <LineItemEditorErrorState /> : null}
      {!loading && !failed ? (
        <LineItemEditorForm
          key={lineItemId ?? 'new'}
          onCancel={() => void returnTo()}
          brandId={brandId}
          campaignId={campaignId}
          client={client}
          currency="USD"
          initialForm={initialForm}
          isEditMode={Boolean(savedLineItemId)}
          savedLineItemId={savedLineItemId}
          savedTargeting={savedTargeting}
          targetingCsvFailures={csvFailures}
          saving={save.isPending}
          targetingClient={targetingClient}
          timezoneLabel="UTC"
          onSubmit={(form) => save.mutateAsync(form).catch(() => undefined)}
        />
      ) : null}
    </DashboardPageFormShell>
  );
}

function resolveInitialForm(lineItem: Parameters<typeof createMadBuyLineItemGeneralForm>[0]) {
  try {
    return createMadBuyLineItemGeneralForm(lineItem, { timezone: 'UTC' });
  } catch {
    return null;
  }
}
